import type { CSSProperties, ElementType, FC, ReactNode } from 'react'

import { Fragment, useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap } from '@/lib/gsap'
import { ease as refEase } from '@/lib/motion'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

interface SplitTextProps {
    lines: string[]
    as?: ElementType
    className?: string
    lineClassName?: string
    delay?: number
    stagger?: number
    duration?: number
    trigger?: boolean
    start?: string
    style?: CSSProperties
}

/**
 * Masked, word-by-word heading reveal.
 *
 * The split happens in render from the `lines` prop rather than by mutating
 * the DOM after mount, so the server and client produce byte-identical markup
 * and hydration stays clean. Each line clips its own overflow and its words
 * rise into place on a stagger.
 */
export const SplitText: FC<SplitTextProps> = ({
    lines,
    as: Tag = 'h2',
    className,
    lineClassName,
    delay = 0,
    stagger = 0.055,
    duration = 1.15,
    trigger = true,
    start = 'top 85%',
    style
}): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const context = gsap.context(() => {
            const words = element.querySelectorAll<HTMLElement>('[data-word]')

            if (!words.length) {
                return
            }

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                gsap.set(element, { visibility: 'visible' })

                return
            }

            gsap.set(element, { visibility: 'visible' })
            gsap.set(words, { yPercent: 118, rotate: 2 })

            gsap.to(words, {
                yPercent: 0,
                rotate: 0,
                duration,
                delay,
                ease: refEase.word,
                stagger,
                scrollTrigger: trigger
                    ? {
                        trigger: element,
                        start,
                        once: true
                    }
                    : undefined
            })
        }, element)

        return () => context.revert()
    }, [lines, delay, stagger, duration, trigger, start])

    return (
        <Tag
            ref={root}
            data-reveal
            className={cn(className)}
            style={style}
        >
            {lines.map((line, lineIndex) => (
                <span key={lineIndex} className={cn('line-mask', lineClassName)}>
                    {line.split(' ').map((word, wordIndex) => (
                        <Fragment key={`${lineIndex}-${wordIndex}`}>
                            {/* The gap between two words is a real text node
                                sitting BETWEEN their masks. Kept inside the
                                inline-block it is trailing whitespace, which
                                browsers discard, and that is what ran headings
                                together. A non-breaking space survives but then
                                refuses to wrap, overflowing narrow screens. */}
                            {wordIndex > 0 ? ' ' : null}

                            <span data-word className='inline-block will-change-transform'>
                                {word}
                            </span>
                        </Fragment>
                    ))}
                </span>
            ))}
        </Tag>
    )
}
